import React, { useEffect, useState } from 'react';
import { Loader2, AlertCircle, Phone, Mail } from 'lucide-react';
import LandingHero from './LandingHero';
import { getBrandConfig } from '../services/widget';
import type { BrandConfig } from '../types';

interface BrandedLandingProps {
  slug: string;
}

/**
 * Public branded landing page served at /v/:slug.
 *
 * Skips auth entirely — anyone with the URL can land here and get a
 * valuation. Captured leads are routed back to the agent who owns
 * the slug via the agent_widget_id thread.
 */
export default function BrandedLanding({ slug }: BrandedLandingProps) {
  const [brand, setBrand] = useState<BrandConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getBrandConfig(slug).then((b) => {
      if (cancelled) return;
      setLoading(false);
      if (!b) {
        setNotFound(true);
      } else {
        setBrand(b);
        document.title = `${b.brand_name} — Free property valuation`;
      }
    });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-[#f47a6b]">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  if (notFound || !brand) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 text-slate-300 px-6 text-center">
        <AlertCircle size={36} className="text-slate-500 mb-3" />
        <h1 className="text-xl font-semibold text-white mb-1">Page not found</h1>
        <p className="text-sm text-slate-500 max-w-md">
          This valuation link doesn't exist or has been paused. Try
          double-checking the URL, or visit{' '}
          <a href="/" className="underline hover:text-white">
            valmate.co.uk
          </a>{' '}
          for an unbranded valuation.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-950">
      {/* Brand header — agent's logo + contact details up top */}
      <header className="px-6 py-4 border-b border-slate-900/80 bg-slate-950/60 backdrop-blur-md">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {brand.logo_url ? (
              <img
                src={brand.logo_url}
                alt={brand.brand_name}
                className="h-9 w-auto object-contain"
              />
            ) : (
              <div
                className="text-lg font-bold tracking-tight text-white truncate"
                style={{ color: brand.primary_colour || '#f47a6b' }}
              >
                {brand.brand_name}
              </div>
            )}
          </div>
          <div className="hidden sm:flex items-center gap-4 text-xs text-slate-400">
            {brand.contact_phone && (
              <a
                href={`tel:${brand.contact_phone.replace(/\s+/g, '')}`}
                className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
              >
                <Phone size={12} />
                {brand.contact_phone}
              </a>
            )}
            {brand.contact_email && (
              <a
                href={`mailto:${brand.contact_email}`}
                className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
              >
                <Mail size={12} />
                {brand.contact_email}
              </a>
            )}
          </div>
        </div>
      </header>

      <LandingHero onSignUp={() => {}} brand={brand} />

      <footer className="px-6 py-6 border-t border-slate-900/80 mt-12">
        <div className="max-w-6xl mx-auto text-center text-xs text-slate-500 space-y-1">
          {brand.disclaimer_text && (
            <div className="text-[11px] text-slate-600 max-w-xl mx-auto leading-relaxed">
              {brand.disclaimer_text}
            </div>
          )}
          {brand.show_powered_by_valmate ? (
            <div>
              Powered by{' '}
              <a href="/" className="text-slate-400 hover:text-white">
                Valmate
              </a>{' '}
              · UK property data via Land Registry
            </div>
          ) : (
            <div className="text-[11px] text-slate-600">
              UK property data via Land Registry
            </div>
          )}
        </div>
      </footer>
    </div>
  );
}
